import React from "react";
import { morphTokenClass, morphPronounSuffixSplit } from "../utils/arabicMorphology";

/**
 * ArabicSentence — renders an analyzed Arabic sentence as a row of tokens.
 *
 * Independent of TopicDetail's renderToken: this is the Iʿrāb tool's display,
 * driven entirely by the analyze/refine response.
 *
 * Props:
 *   text            the sentence EXACTLY as echoed by the backend (NFC) —
 *                   indices must line up with text.split(/\s+/)
 *   tokens          { "<idx>": { morphology, source, refinable } }
 *   colorToggles    { verb, noun, pronoun, prepositions }
 *   refiningIndices Set of "<idx>" currently being AI-refined
 *   onTokenClick    (idxStr, morphology) => void
 */
export default function ArabicSentence({
  text,
  tokens,
  colorToggles,
  refiningIndices,
  onTokenClick,
}) {
  const parts = (text || "").split(/\s+/).filter(Boolean);

  return (
    <div
      dir="rtl"
      lang="ar"
      className="flex flex-wrap gap-x-2 gap-y-3 justify-start font-amiri text-3xl leading-loose"
    >
      {parts.map((tok, i) => {
        const key = String(i);
        const entry = tokens?.[key];
        const morph = entry?.morphology || null;
        const refining = refiningIndices?.has(key);
        const refined = entry?.source === "openai";
        const cls = morph ? morphTokenClass(morph, colorToggles) : "";
        const pronSplit =
          morph && colorToggles.pronoun ? morphPronounSuffixSplit(tok, morph) : null;

        const inner = pronSplit ? (
          <>
            <span className={cls || undefined}>{pronSplit.stem}</span>
            <span className="tokc-pron-suffix">{pronSplit.suffix}</span>
            {pronSplit.tail}
          </>
        ) : (
          <span className={cls || undefined}>{tok}</span>
        );

        if (!morph) {
          return (
            <span key={key} className="relative text-dt-parchment">
              {inner}
              {refining && <TokenSpinner />}
            </span>
          );
        }
        return (
          <button
            key={key}
            type="button"
            onClick={() => onTokenClick?.(key, morph)}
            title={refined ? "AI-checked — tap for the full iʿrāb" : "Tap for the word structure"}
            className="relative text-dt-parchment hover:text-dt-gold-bright transition-colors cursor-pointer bg-transparent border-0 p-0 font-amiri text-3xl leading-loose"
          >
            {inner}
            {refining && <TokenSpinner />}
            {refined && !refining && (
              <span
                aria-label="AI checked"
                className="absolute -top-1 -left-2 text-[10px] leading-none select-none"
                style={{ color: "var(--gold)" }}
              >
                ✓
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function TokenSpinner() {
  return (
    <span
      role="status"
      aria-label="Refining"
      className="absolute -top-1 -left-3 inline-block w-2.5 h-2.5 rounded-full animate-spin"
      style={{ border: "2px solid var(--gold-dim)", borderTopColor: "var(--gold)" }}
    />
  );
}
